import { useEffect, useState } from 'react';
import { apiGet } from 'src/lib/api/axios';

/**
 * Eager hover-driven fetch of the lightweight metadata surfaced by
 * {@link ../components/ResourcePopover}. Matches the shape of the
 * backend ``ResourcePreview`` schema (``src/resources/schemas.py``).
 *
 * The backend emits a discriminated union keyed by ``kind`` for the
 * ``metadata`` field — each resource type has a bespoke shape and a
 * matching renderer under ``components/ResourcePopover/``. Adding a
 * new type means extending both this union and the renderer switch.
 */

export interface ActionPreviewUpdate {
  timestamp?: string | null;
  text: string;
  author?: string | null;
}

export interface DocumentPreviewMetadata {
  kind: 'document';
  is_wiki: boolean;
  reference?: string | null;
  document_type?: string | null;
  publication_date?: string | null;
  modified_at?: string | null;
  category?: string | null;
  domain?: string | null;
  subdomain?: string | null;
  authority?: string | null;
  jurisdiction?: string | null;
  issuing_body?: string | null;
  summary?: string | null;
  /** Non-wiki only: AI-extracted human-readable title. Prefer over the
   * stored filename when displaying the document in the popover header. */
  extracted_title?: string | null;
  /** Non-wiki only: ULID of the underlying document. Used by AnalysisTagsCell
   * so the popover can reuse the same tag pills as the Document Hub. */
  document_id?: string | null;
  /** Non-wiki only: raw storage document_type (e.g. "upload",
   * "tenant_file"). Frontend maps to space name via friendlySource. */
  source_type?: string | null;
  /** Non-wiki only: when the document was uploaded / and by whom.
   * Wiki entries leave these null — their provenance is the
   * publication year + authority shown in the subtitle. */
  created_at?: string | null;
  created_by_name?: string | null;
  /** Non-wiki only: team groups the document has been shared with
   * (via v2 resource permissions). Empty when private or shared
   * only with individual users. */
  shared_with_groups?: string[];
}

export interface ObligationPreviewMetadata {
  kind: 'obligation';
  party: string;
  obligation_kind: string;
  schedule: string;
  lifecycle: string;
  health: string;
  clause_ref?: string | null;
  group_name?: string | null;
  group_id?: string | null;
  modified_at?: string | null;
}

export interface ActionPreviewMetadata {
  kind: 'action';
  reference: string;
  status: string;
  due_date?: string | null;
  priority?: string | null;
  modified_at?: string | null;
  assignees: string[];
  description?: string | null;
  updates: ActionPreviewUpdate[];
}

export interface MeetingPreviewMetadata {
  kind: 'meeting';
  status: string;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  location?: string | null;
  series_title?: string | null;
  group_title?: string | null;
  modified_at?: string | null;
  summary?: string | null;
}

export interface MeetingSeriesPreviewMetadata {
  kind: 'meeting_series';
  group_title?: string | null;
  /** ULID of the group the series belongs to. Used by the
   * ``/meeting_series/:id`` redirect route to deep-link a series
   * citation straight into the group's Meetings tab — there's no
   * standalone "series page" to navigate to. */
  group_id?: string | null;
  meeting_count?: number | null;
  next_meeting_date?: string | null;
  modified_at?: string | null;
}

export interface TeamGroupPreviewMetadata {
  kind: 'team_group';
  short_reference?: string | null;
  description?: string | null;
  modified_at?: string | null;
  /** Distinct members across Admin / Contributor / Viewer roles.
   * Null for a freshly-created group with no roles provisioned. */
  member_count?: number | null;
  /** Current viewer's group role label (e.g. "Admin", "Member",
   * "Viewer"). Null when the viewer isn't a direct member and only
   * sees the group via platform / resource-share access. */
  viewer_role?: string | null;
  /** OPEN actions in the group assigned to the current viewer.
   * Always defined (defaults to 0), drives the "You have X actions
   * in this group" popover row. */
  viewer_open_action_count?: number;
  /** Scheduled date of the most recent past meeting. Null when the
   * group has never run a completed or cancelled meeting. */
  last_meeting_date?: string | null;
  /** ULID of that meeting, used to render the "Last meeting …"
   * popover row as a navigable link. Always paired with
   * ``last_meeting_date`` — both set, or both absent. */
  last_meeting_id?: string | null;
}

export interface ReviewPreviewMetadata {
  kind: 'review';
  review_type?: string | null;
  status?: string | null;
  compliance_score?: number | null;
  started_at?: string | null;
  modified_at?: string | null;
  summary?: string | null;
  document_title?: string | null;
}

export interface WritingPreviewMetadata {
  kind: 'writing';
  modified_at?: string | null;
  /** User-facing name of the writing template ("Meeting minutes",
   * "Operational report"). Null for custom writings, where the
   * resource title already conveys what it is. */
  writing_type?: string | null;
}

export interface ChatPreviewMetadata {
  kind: 'chat';
  started_at?: string | null;
  modified_at?: string | null;
  /** Truncated opening exchange surfaced in the hover popover —
   * first user question + first assistant reply, each capped
   * server-side. Rich parts (files, tool calls, citations) are
   * deliberately stripped: the preview is about intent, not
   * transcript fidelity. Either field may be null for new or
   * partial conversations. */
  first_user_message?: string | null;
  first_assistant_message?: string | null;
}

export interface SpecialistPreviewMetadata {
  kind: 'specialist';
  modified_at?: string | null;
  colour?: string | null;
  /** Truncated opening excerpt of the specialist's instruction
   * prompt. Gives the hover reader a sense of what this specialist
   * does before clicking through. */
  instruction_excerpt?: string | null;
  /** Response-style guidance (e.g. "Be concise and use bullet
   * points"). Null when the specialist has no style constraint. */
  response_style?: string | null;
  creator_name?: string | null;
}

export interface DashboardPreviewMetadata {
  kind: 'dashboard';
  modified_at?: string | null;
  /** Human-readable label for the period the aggregated level was
   * calculated for (e.g. "April 2026"). Null when the dashboard has
   * no data yet. */
  period_label?: string | null;
  /** Root card cadence — labels the history chart and the
   * "vs last month/quarter/week" copy. */
  update_cadence?: 'monthly' | 'quarterly' | 'weekly' | null;
  /** Calendar period the history trail is anchored on. The popover
   * walks backwards from these to label each bar. */
  period_year?: number | null;
  period_month?: number | null;
  period_week?: number | null;
  /** Aggregated assurance level for the latest period, as the raw
   * backend enum value ("high" / "substantial" / ...). Null when no
   * data exists. */
  assurance_level?: string | null;
  /** Previous-period level — drives the "Improved / Declined from
   * <level>" delta row. Null when no prior data. */
  previous_assurance_level?: string | null;
  /** Trail of levels ordered oldest → newest, with the current
   * period appended last. Entries are null where no data exists for
   * that period. Empty when the dashboard has never recorded any
   * data. */
  historical_assurance_levels: (string | null)[];
  /** Bar history spec from the backend. Null when the trail is empty. */
  chart_spec?: Record<string, unknown> | null;
  /** Classic dial from the backend. Null when the dashboard has no calculated level yet. */
  gauge_spec?: Record<string, unknown> | null;
}

export interface AssetReviewUploadInfo {
  filename: string;
  document_id?: string | null;
}

export interface AssetReviewPreviewMetadata {
  kind: 'asset_review';
  modified_at?: string | null;
  total_observations: number;
  reviewed_observations: number;
  /** Condition grade → count, e.g. {"A": 3, "B": 5, "C": 2} */
  grade_counts: Record<string, number>;
  uploads: AssetReviewUploadInfo[];
}

export interface AppointmentEvidencePreview {
  title: string;
  /** Evidence Status. An Evidence Document is always on the platform, so never ``missing``. */
  status: 'expired' | 'pending' | 'expiring' | 'valid';
  /** Pre-formatted. Absent when the hub holds no issue date. */
  issued_on?: string | null;
  /** Pre-formatted. Absent when the document never lapses. */
  expires_on?: string | null;
}

export interface TrainingAppointmentPreviewMetadata {
  kind: 'training_appointment';
  appointee_name: string;
  appointee_email?: string | null;
  domain_name: string;
  role_name: string;
  /** `null` = trust-wide (no site scope). */
  site_name?: string | null;
  appointed_from?: string | null;
  /** `null` = open-ended (no scheduled end). */
  appointed_until?: string | null;
  /** "pending" / "active" / "expired" / "revoked". */
  status: string;
  /** Most severe first. Empty when nothing is attached. */
  evidence_documents: AppointmentEvidencePreview[];
  modified_at?: string | null;
  /** Permission-aware open href resolved server-side. Falls back to
   * the static ``RESOURCE_PATH`` entry if absent. */
  open_href?: string | null;
  /** Human label for the open button, e.g. "Open in Training Hub" or
   * "Open in Water Safety Group". */
  open_label?: string | null;
}

export interface PamSubmissionQuestionPreviewMetadata {
  kind: 'pam_submission_question';
  /** Question reference code, e.g. "C1.1". */
  question_ref: string;
  /** Truncated question text for the body preview. */
  question_text?: string | null;
  /** Parent SAQ acronym, e.g. "C1". */
  saq_acronym: string;
  /** Parent SAQ name, e.g. "Performance Management". */
  saq_name: string;
  /** Human-readable SAQ status: "Not started" / "In progress" / "Under review" / "Approved". */
  saq_status: string;
  /** "YES" / "NO" / "N/A" when committed; null when not yet answered. */
  final_answer?: string | null;
  assignees: string[];
  modified_at?: string | null;
  /** Server-resolved path ``/modules/pam/q/{questionId}``. */
  open_href?: string | null;
}

export interface PamSubmissionPreviewMetadata {
  kind: 'pam_submission';
  /** Human-readable status label: "In progress", "Finalised", "Submitted". */
  status: string;
  year: number;
  owner_name?: string | null;
  /** Questions with a committed final answer vs total materialised questions. */
  questions_answered: number;
  questions_total: number;
  /** SAQs in APPROVED status vs total materialised SAQs. */
  saqs_approved: number;
  saqs_total: number;
  /** Earliest upcoming deadline across non-approved SAQs, pre-formatted. */
  next_deadline?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  modified_at?: string | null;
}

export interface GenericPreviewMetadata {
  kind: 'generic';
  type_label: string;
  modified_at?: string | null;
}

export type ResourcePreviewMetadata =
  | DocumentPreviewMetadata
  | ActionPreviewMetadata
  | ObligationPreviewMetadata
  | MeetingPreviewMetadata
  | MeetingSeriesPreviewMetadata
  | TeamGroupPreviewMetadata
  | ReviewPreviewMetadata
  | WritingPreviewMetadata
  | ChatPreviewMetadata
  | SpecialistPreviewMetadata
  | AssetReviewPreviewMetadata
  | DashboardPreviewMetadata
  | TrainingAppointmentPreviewMetadata
  | PamSubmissionQuestionPreviewMetadata
  | PamSubmissionPreviewMetadata
  | GenericPreviewMetadata;

export interface ResourcePreview {
  id: string;
  resource_type: string;
  title: string;
  metadata: ResourcePreviewMetadata;
}

/** Possible states of a preview fetch for a given resource ID. */
export type PreviewStatus = 'loading' | 'ready' | 'not_found' | 'error';

export interface PreviewState {
  status: PreviewStatus;
  data?: ResourcePreview;
  error?: string;
}

// Module-level cache keyed by resource ULID. Previews are immutable
// enough for a chat session (the user isn't editing the cited resource
// from inside the popover) that we can reuse them across every
// ResourceLink render without invalidation; a page refresh is the
// natural cache-bust.
const cache = new Map<string, PreviewState>();
// Pending in-flight requests — de-duped so two hovers over the same
// citation don't issue two network calls.
const inflight = new Map<string, Promise<PreviewState>>();

type Listener = (state: PreviewState) => void;
const listeners = new Map<string, Set<Listener>>();

function emit(resourceId: string, state: PreviewState) {
  cache.set(resourceId, state);
  const set = listeners.get(resourceId);
  if (set) {
    set.forEach((fn) => fn(state));
  }
}

async function fetchPreview(resourceId: string): Promise<PreviewState> {
  try {
    const response = await apiGet<ResourcePreview>(`resources/${resourceId}/preview`, {
      meta: { globalError: false, context: 'resource-preview', background: true }
    });
    return { status: 'ready', data: response.data };
  } catch (err) {
    // 404 is an ordinary outcome (resource deleted, private to another
    // user); surface it distinctly so the popover can render a terse
    // "no longer available" message rather than a scary error banner.
    const status = (err as any)?.response?.status;
    if (status === 404) {
      return { status: 'not_found' };
    }
    const message = err instanceof Error ? err.message : 'Failed to load preview';
    return { status: 'error', error: message };
  }
}

/**
 * Trigger (or reuse) a preview fetch for ``resourceId``. Safe to call
 * multiple times — subsequent calls before the first resolves return
 * the shared in-flight promise, and once cached further calls are
 * instant.
 */
export function prefetchResourcePreview(resourceId: string): Promise<PreviewState> {
  const cached = cache.get(resourceId);
  if (cached && cached.status !== 'loading') {
    return Promise.resolve(cached);
  }
  const existing = inflight.get(resourceId);
  if (existing) return existing;

  emit(resourceId, { status: 'loading' });
  const promise = fetchPreview(resourceId).then((state) => {
    inflight.delete(resourceId);
    emit(resourceId, state);
    return state;
  });
  inflight.set(resourceId, promise);
  return promise;
}

/**
 * React hook for a single resource preview. The fetch only fires once
 * ``shouldLoad`` flips to ``true`` — the {@link ../components/ResourceLink}
 * call site wires this to "user has hovered at least once" so idle
 * citations don't all fire network requests on page load.
 */
export function useResourcePreview(resourceId: string, shouldLoad: boolean): PreviewState {
  const [state, setState] = useState<PreviewState>(() => cache.get(resourceId) ?? { status: 'loading' });

  useEffect(() => {
    if (!shouldLoad) return;

    const set = listeners.get(resourceId) ?? new Set<Listener>();
    set.add(setState);
    listeners.set(resourceId, set);

    // Seed with cached state (or kick off the fetch) so the first
    // render after mount reflects whatever we already know.
    const cached = cache.get(resourceId);
    if (cached) {
      setState(cached);
      if (cached.status === 'error') {
        // Retry on remount for transient failures — the user hovering
        // again is a reasonable "please try that again" signal.
        prefetchResourcePreview(resourceId);
      }
    } else {
      prefetchResourcePreview(resourceId);
    }

    return () => {
      set.delete(setState);
      if (set.size === 0) {
        listeners.delete(resourceId);
      }
    };
  }, [resourceId, shouldLoad]);

  return state;
}
